import React, { cloneElement, CSSProperties, ReactElement } from 'react'

import { css, useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import { ArcadeIcon, PushIcon } from '@arcade/web/icons/react'

import { useHelpTextId } from '../../../hooks/useHelpTextId'
import { ThemeSpacing } from '../../../theme/spacing'
import { hasChildOfType } from '../../../utils/hasChildOfType'
import { ArcadeAvatar, Grid } from '../..'
import { Avatar } from '../../Avatar/Avatar'
import { Badge } from '../../Badge/Badge'
import { Flex, FullWidthFlex } from '../../layout/Flex'
import Text from '../../Text/Text'
import ArcadeButton from '../Button/ArcadeButton'
import ArcadeHelpText, { ArcadeHelpTextProps, combineAriaDescribedBy } from '../Input/ArcadeHelpText'
import ArcadeRadioButton from '../RadioButton/ArcadeRadioButton'
import { Toggle } from '../Toggle/Toggle'

export interface BaseCellAsset {
  Icon?: ArcadeIcon
  avatar?: ReactElement
}

export interface BaseCellLabelProps extends React.ComponentProps<typeof Text> {
  /**
   * Optional: Style the label when it overflows the first line
   * If this is not set, we wrap the label to the next line by default
   */
  labelOverflow?: 'ellipsis'
}

export interface BaseCellProps extends Omit<ArcadeHelpTextProps, 'disabled' | 'marginTop'> {
  label: string
  labelIcon?: React.ReactElement<ArcadeIcon>
  labelProps?: BaseCellLabelProps
  body?: React.ReactNode
  /**
   * Optional: Asset to display on the left side of the cell, i.e. an icon or an avatar.
   */
  asset?: BaseCellAsset
  /**
   * Optional: Accessory to display on the right side of the cell.
   */
  accessory?: ReactElement
  /**
   * Optional: Alignment of the accessory.
   * If not provided, it will be determined based on the accessory type.
   * Botton and toggles will be aligned to the center, all other types will be aligned to the flex-start.
   */
  accessoryAlignment?: CSSProperties['alignItems']
  /**
   * Optional: Badge to display on the left side of accessory.
   * Only supported with push or radio accessory.
   */
  badge?: Omit<React.ComponentProps<typeof Badge>, 'variant'>
  disabled?: boolean
  cellTestId?: string
  headerSpacing?: ThemeSpacing[keyof ThemeSpacing]
  as?: React.ElementType
  'aria-labelledby'?: string
  'aria-describedby'?: string
}

interface TestIdProps {
  'data-testid'?: string
}

const DISABLED_OPACITY = 0.4
const Opacity = styled.div<{ opacity?: number }>`
  opacity: ${props => props.opacity};
`

const LabelText = styled(Text)<{ labelOverflow?: 'ellipsis' }>`
  ${({ labelOverflow }) =>
    labelOverflow === 'ellipsis' &&
    css`
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
    `}
`

/**
 * Asset styles (icon or avatar)
 */
const getIcon = (Icon: ArcadeIcon, disabled: boolean, cellTestId?: string) => (
  <Icon
    size={24}
    variant={disabled ? 'disabled' : 'standard'}
    data-testid={cellTestId ? `cell-icon-${cellTestId}` : 'cell-icon'}
  />
)

const getLabelIcon = (icon: React.ReactElement<ArcadeIcon>, disabled: boolean, cellTestId?: string) => (
  <Opacity
    opacity={disabled ? DISABLED_OPACITY : undefined}
    data-testid={cellTestId ? `cell-label-icon-${cellTestId}` : 'cell-label-icon'}
  >
    <Flex alignItems="center">{icon}</Flex>
  </Opacity>
)

const IconWrapper = styled.div`
  height: 1.5rem;
  width: 1.5rem;
`

const BaseCell = React.forwardRef<HTMLDivElement, BaseCellProps & TestIdProps>((props, ref) => {
  const { spacing } = useTheme()
  const {
    label,
    labelIcon,
    labelProps = {},
    body,
    asset,
    accessory,
    accessoryAlignment,
    badge,
    disabled = false,
    'data-testid': baseCellTestId,
    cellTestId,
    headerSpacing = spacing.empty,
    as,
    'aria-labelledby': ariaLabelledby,
    'aria-describedby': ariaDescribedBy,
    helpTextId: providedHelpTextId,
    helpText,
    HelpTextIcon,
    isInvalid = false,
    errorText,
    disabledText,
  } = props
  const helpTextId = useHelpTextId({ helpTextId: providedHelpTextId, helpText, errorText, disabledText })

  // Extract labelOverflow from labelProps
  const { labelOverflow, ...restLabelProps } = labelProps

  /**
   * if data-testid is directly provided, use it: baseCellTestId. Otherwise, if a cellTestId is provided/passed down from an outer component, use that.
   */
  const testId = baseCellTestId || cellTestId

  const shouldCenterAlignCell = !body && (asset?.avatar || asset?.Icon)

  //  `avatar` can be a CDS Avatar or a custom component. If it's a CDS Avatar, use the `disabled` field. For custom components, simulate disabled state with opacity.
  const renderAvatar = () => {
    if (asset?.avatar?.type === Avatar || asset?.avatar?.type === ArcadeAvatar) {
      return cloneElement(asset.avatar, { disabled })
    } else if (asset?.avatar) {
      return <Opacity opacity={disabled ? DISABLED_OPACITY : undefined}>{asset?.avatar}</Opacity>
    }
    return null
  }

  const renderAccessory = () => (accessory ? cloneElement(accessory, { disabled }) : null)

  const getAlignment = () => {
    if (shouldCenterAlignCell) {
      return 'center'
    }
    return asset?.avatar ? 'flex-start' : 'initial'
  }

  const getAccessoryAlignment = () => {
    if (shouldCenterAlignCell || hasChildOfType(accessory, ArcadeButton) || hasChildOfType(accessory, Toggle)) {
      return 'center'
    }
    return 'flex-start'
  }

  const shouldShowBadge = badge && (hasChildOfType(accessory, PushIcon) || hasChildOfType(accessory, ArcadeRadioButton))

  return (
    <>
      <Flex
        {...props}
        alignItems={getAlignment()}
        gap={spacing.m}
        ref={ref}
        data-testid={testId}
        {...(as && { as })}
        {...(ariaLabelledby && { 'aria-labelledby': ariaLabelledby })}
        aria-describedby={combineAriaDescribedBy(ariaDescribedBy, helpTextId)}
      >
        {/* The div allows the asset to maintain its size in a flex box */}
        {asset?.Icon ? (
          <IconWrapper>{asset?.Icon && getIcon(asset.Icon, disabled, testId)}</IconWrapper>
        ) : asset?.avatar ? (
          <div>{renderAvatar()}</div>
        ) : null}
        <FullWidthFlex flexDirection="column" gap={headerSpacing}>
          <Grid
            gridTemplateColumns={labelIcon ? 'minmax(0, min-content) auto' : undefined}
            alignItems="center"
            columnGap="xxs"
          >
            <LabelText
              typography="labelMedium"
              color={disabled ? 'cellLabelDisabled' : 'cellLabelDefault'}
              labelOverflow={labelOverflow}
              {...restLabelProps}
            >
              {label}
            </LabelText>
            {labelIcon && getLabelIcon(labelIcon, disabled, testId)}
          </Grid>

          {body && (
            <Text typography="bodySmall" color={disabled ? 'cellBodyDisabled' : 'cellBodyDefault'}>
              {body}
            </Text>
          )}
        </FullWidthFlex>
        {accessory ? (
          <Flex
            alignSelf={accessoryAlignment ?? getAccessoryAlignment()}
            alignItems="center"
            gap={React.isValidElement(accessory) && accessory.type === PushIcon ? undefined : spacing.m}
          >
            {shouldShowBadge && (
              <Opacity opacity={disabled ? DISABLED_OPACITY : undefined}>
                <Badge variant="inline" {...badge} />
              </Opacity>
            )}
            {renderAccessory()}
          </Flex>
        ) : null}
      </Flex>
      <ArcadeHelpText
        helpTextId={helpTextId}
        helpText={helpText}
        HelpTextIcon={HelpTextIcon}
        isInvalid={isInvalid}
        errorText={errorText}
        disabled={disabled}
        disabledText={disabledText}
      />
    </>
  )
})

export default BaseCell
